import { useState, useEffect, useRef } from "react";
import { ArrowUp } from "lucide-react";
import { Button } from "./button";
import { cn } from "@/lib/utils";
import { triggerHaptic } from "@/utils/haptics";

interface ScrollToTopButtonProps {
  hasStickyContent?: boolean;
}

export const ScrollToTopButton = ({ hasStickyContent = false }: ScrollToTopButtonProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('down');
  const [hasInitialized, setHasInitialized] = useState(false);
  const ticking = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (!hasInitialized && currentScrollY > 0) {
        setHasInitialized(true);
      }
      
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          // Determine scroll direction
          if (currentScrollY > lastScrollY) {
            setScrollDirection('down');
          } else if (currentScrollY < lastScrollY) {
            setScrollDirection('up');
          }
          
          // Show button when scrolling up and past 300px
          // Hide when scrolling down or near top
          if (scrollDirection === 'up' && currentScrollY > 300) {
            setIsVisible(true);
          } else if (scrollDirection === 'down' || currentScrollY < 100) {
            setIsVisible(false);
          }
          
          setLastScrollY(currentScrollY);
          ticking.current = false;
        });
        
        ticking.current = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY, scrollDirection, hasInitialized]);

  const scrollToTop = () => {
    triggerHaptic('medium');
    
    // If already near top, use instant scroll to avoid animation restart issue
    const behavior = window.scrollY < 150 ? 'instant' : 'smooth';
    
    window.scrollTo({
      top: 0,
      behavior,
    });
  };

  return (
    <Button
      onClick={scrollToTop}
      size="icon"
      className={cn(
        "fixed right-4 z-40 rounded-full shadow-lg",
        "h-14 w-14 lg:h-12 lg:w-12",
        hasStickyContent ? "bottom-24" : "bottom-4",
        !hasInitialized && "hidden",
        isVisible 
          ? "animate-[slideInUp_300ms_ease-out_forwards]" 
          : "animate-[slideOutDown_300ms_ease-out_forwards] pointer-events-none"
      )}
      aria-label="Scrolla till toppen"
    >
      <ArrowUp className="h-6 w-6 lg:h-5 lg:w-5" />
    </Button>
  );
};
